import React, { useEffect, useState } from 'react'
import './AnalyzerGenerator.css'
import {
  Col,
  Row,
  Form,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from 'reactstrap'
import Joi from 'joi'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { createEntityAdapter } from '@reduxjs/toolkit'
import {
  getAllDatasets,
  getAllScript,
  getConfigs,
  createAnalyzer,
  updateAnalyzer,
  createJob,
  setEntityError,
} from '../../../redux/slices'
import { DefaultState } from '../../../types'
import ICSTextInput from '../../../components/ICSTextInput/ICSTextInput'
import ICSTextAreaInput from '../../../components/ICSTextAreaInput/ICSTextAreaInput'
import ICSSelectInput from '../../../components/ICSSelectInput/ICSSelectInput'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import ICSDateInput from '../../../components/ICSDateInput/ICSDateInput'
import ICSButtonGroup from '../../../components/ICSButtonGroup/ICSButtonGroup'
import ICSCronInput from '../../../components/ICSCronInput/ICSCronInput'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import { AppDispatch } from '../../../services/lib'
import { hasInvalidField, invalidFieldsMessage } from '../../../utils/helpers'
import { IPAScript } from '../../../types/scripts'
import { DataSet } from '../../../pages/Datasets/types'
import { IPAConfig } from '../../../types'
import { ICSModal } from '../../../components'
import ComponentBoxFooter from '../../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'

const defaultJoiSchema = {
  type: Joi.array().items(Joi.string()).required().messages({
    'array.empty': `Required field`,
  }),
  script: Joi.string().required().messages({
    'string.empty': `Required field`,
  }),
  config: Joi.string().required().messages({
    'string.empty': `Required field`,
  }),
  datasets: Joi.string().required().messages({
    'string.empty': `Required field`,
  }),
  name: Joi.string().required().min(3).max(30).messages({
    'string.empty': `Required field`,
    'string.min': `Must be at lease {#limit} in length`,
  }),
  description: Joi.string().min(0).max(255).allow(''),
  interval: Joi.when('type', {
    is: Joi.array().items(Joi.string().valid('Manual')),
    then: Joi.string().allow(''),
    otherwise: Joi.string()
      .required()
      .custom((value, helpers) => {
        const cronPattern = new RegExp(
          /^(\*|([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])|\*\/([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])) (\*|([0-9]|1[0-9]|2[0-3])|\*\/([0-9]|1[0-9]|2[0-3])) (\*|([1-9]|1[0-9]|2[0-9]|3[0-1])|\*\/([1-9]|1[0-9]|2[0-9]|3[0-1])) (\*|([1-9]|1[0-2])|\*\/([1-9]|1[0-2])) (\*|([0-6])|\*\/([0-6]))$/
        )
        if (!cronPattern.test(value)) {
          return helpers.error('any.invalid')
        } else {
          return value
        }
      })
      .messages({
        'string.empty': `Required field`,
      }),
  }),
  startSchedule: Joi.when('type', {
    is: Joi.array().items(Joi.string().valid('Manual')),
    then: Joi.any(),
    otherwise: Joi.string().required().messages({
      'string.empty': `Required field`,
    }),
  }),
}

const schema = Joi.object().keys(defaultJoiSchema)

const AnalyzerGenerator = ({ location, hideModal, showModal }) => {
  const isEdit = location?.state && location?.state?.isEdit
  const selectedAnalyzer = isEdit ? location.state.analyzer : null
  //const { accessToken } = useSelector((state: DefaultState) => state.session.data)
  const analyzersError = useSelector(
    (state: DefaultState) => state.analyzers.error
  )

  const {
    formState: { errors, isSubmitted, isValid },
    watch,
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    trigger,
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema),
  })

  const watchScriptInput = watch('script')
  const watchTypeInput = watch('type')
  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const [showFrequency, setShowFrequency] = useState(false)
  const datasets = useSelector((state: DefaultState) => state.datasets)
  const scripts = useSelector((state: DefaultState) => state.scripts)
  const configs = useSelector((state: DefaultState) => state.configs)
  const datasetsState = createEntityAdapter<DataSet>()
    .getSelectors()
    .selectAll(datasets)
  const scriptsState = createEntityAdapter<IPAScript>()
    .getSelectors()
    .selectAll(scripts)
  const configsState = createEntityAdapter<IPAConfig>()
    .getSelectors()
    .selectAll(configs)

  useEffect(() => {
    // prevents console error for invalid token
    // todo - this shouldn't be here
    if (window.location.pathname != '/ui2/login') {
      //dispatch(setProfile(accessToken))
      dispatch(getAllDatasets())
      dispatch(getAllScript())
      if (isEdit) {
        dispatch(getConfigs(selectedAnalyzer.script.id))
      }
    }
  }, [dispatch, isEdit, selectedAnalyzer?.script.id])

  useEffect(() => {
    if (analyzersError) {
      analyzersError.fields.forEach(async errorObj =>
        setError(errorObj.field, {
          type: 'manual',
          message: errorObj.message,
        })
      )
    }
  }, [analyzersError, setError])

  useEffect(() => {
    if (getValues()?.script) dispatch(getConfigs(getValues().script))
  }, [watchScriptInput, getValues, dispatch])

  const selectedAnalyzerType =
    selectedAnalyzer?.type === 'periodic' ? 'Periodic' : 'Manual'

  useEffect(() => {
    setShowFrequency(getValues('type')?.[0] === 'Periodic')
  }, [watchTypeInput, getValues])

  const generate = async () => {
    const {
      name,
      type,
      script,
      datasets,
      config,
      description,
      startSchedule,
      interval,
    } = getValues()

    const body = {
      name,
      script_id: script,
      configs: [config],
      datasets: [datasets],
      type: type[0].toLowerCase(),
      frequency: {
        interval,
        start_schedule:
          startSchedule !== '' ? startSchedule : '0000-00-00T00:00:00.000Z',
      },
      description,
    }
    let newAnalyzer

    if (!isEdit) {
      newAnalyzer = await dispatch(createAnalyzer(body))
    } else {
      const { id } = selectedAnalyzer
      newAnalyzer = await dispatch(updateAnalyzer({ id, changes: body }))
    }

    if (newAnalyzer.type.split('/').pop() === 'fulfilled') {
      if (body.type === 'manual') {
        const r = window.confirm(
          `Run ${isEdit ? 'Updated' : 'Created'} Analyzer?`
        )
        if (r === true && isEdit) {
          dispatch(createJob(selectedAnalyzer.id))
        } else if (r === true && !isEdit && newAnalyzer.payload.id) {
          dispatch(createJob(newAnalyzer.payload.id))
        }
      }

      hideModal()
      navigate('/ui2/analyzers')
    } else {
      const verifyFields = await hasInvalidField(
        Object.keys(defaultJoiSchema),
        newAnalyzer.payload.fields.map(
          (fieldObj: { field: string; message: string }) => fieldObj.field
        )
      )

      if (verifyFields.isInvalid) {
        dispatch(
          setEntityError({
            sliceName: 'analyzers',
            entityName: 'Analyzer',
            error: {
              code: 'error',
              message: invalidFieldsMessage,
              fields: verifyFields.fields,
            },
          })
        )
      }
    }
  }

  return (
    <>
      <ICSModal
        visible={showModal}
        onHiding={hideModal}
        title='Create New Analyzer'
        position='center'
        id='analyzerModal'
      >
        <ModalBody style={{padding:'0px'}}>
          <Row>
            <Col xs={{ size: 12 }} className='wivi-col'>
              {analyzersError?.code === 'error' ? (
                <ICSErrorCard
                  code={analyzersError.code}
                  message={analyzersError.message}
                  fields={analyzersError.fields}
                />
              ) : (
                <Form onSubmit={handleSubmit(generate)}>
                  <FormGroup>
                    <Row>
                      <Col>
                        <ICSButtonGroup
                          name='type'
                          label='Type'
                          items={[
                            {
                              text: 'Manual',
                              hint: 'A static analyzer that can be run manually',
                            },
                            // {
                            //   text: 'Periodic',
                            //   hint: 'A periodic analyzer that will be run periodically based on interval',
                            // },
                          ]}
                          errors={errors}
                          control={control}
                          defaultSelectedKeys={[
                            isEdit ? selectedAnalyzerType : 'Manual',
                          ]}
                          // helpText='Select whether or not the analyzer will be run on a periodic or one-time basis'
                        />
                      </Col>
                    </Row>
                    <Row className={showFrequency ? '' : 'd-none'}>
                      <Col>
                        <ICSDateInput
                          name='startSchedule'
                          id='startSchedule'
                          control={control}
                          errors={errors}
                          isSubmitted={isSubmitted}
                          label='Start Schedule'
                          getValues={getValues}
                          setValue={setValue}
                          defaultValue={
                            isEdit
                              ? selectedAnalyzer?.frequency.start_schedule
                              : ''
                          }
                        />
                      </Col>
                    </Row>
                    <Row className={showFrequency ? '' : 'd-none'}>
                      <Col>
                        <ICSTextInput
                          label='Frequency'
                          name='interval'
                          helpText='Simple CRON format (ex: "* * * * *" for once every minute)'
                          defaultValue={isEdit ? selectedAnalyzer.interval : ''}
                          maxCharCount={30}
                          isSubmitted
                          errors={errors}
                          control={control}
                          setValue={setValue}
                          getValues={getValues}
                          setError={setError}
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <ICSTextInput
                          label='Name'
                          name='name'
                          placeholder={
                            isEdit ? selectedAnalyzer.name : 'Analyzer Name ...'
                          }
                          defaultValue={isEdit ? selectedAnalyzer.name : ''}
                          maxCharCount={30}
                          isSubmitted
                          errors={errors}
                          control={control}
                          setValue={setValue}
                          getValues={getValues}
                          setError={setError}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <ICSTextAreaInput
                          label='Description'
                          name='description'
                          id='description'
                          errors={errors}
                          control={control}
                          setValue={setValue}
                          getValues={getValues}
                          setError={setError}
                          placeholder={
                            isEdit
                              ? selectedAnalyzer.description
                              : 'Analyzer Description ...'
                          }
                          defaultValue={
                            isEdit ? selectedAnalyzer.description : ''
                          }
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <ICSSelectInput
                          label='Script'
                          name='script'
                          placeholder={
                            isEdit
                              ? selectedAnalyzer.script.name
                              : 'Select Script'
                          }
                          defaultValue={
                            isEdit ? selectedAnalyzer.script.id : ''
                          }
                          options={scriptsState.map(script => {
                            return {
                              valueField: script.id,
                              displayField: script.name,
                            }
                          })}
                          autoComplete='on'
                          errors={errors}
                          control={control}
                          setValue={setValue}
                          setError={setError}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <ICSSelectInput
                          name='config'
                          label='Configuration'
                          placeholder={
                            isEdit
                              ? selectedAnalyzer.configs[0].name
                              : 'Select Config'
                          }
                          defaultValue={
                            isEdit ? selectedAnalyzer.configs[0].id : ''
                          }
                          options={configsState.map(config => {
                            return {
                              valueField: config.id,
                              displayField: config.name,
                            }
                          })}
                          autoComplete='on'
                          errors={errors}
                          control={control}
                          setValue={setValue}
                          setError={setError}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <ICSSelectInput
                          label='Dataset'
                          name='datasets'
                          placeholder={
                            isEdit
                              ? selectedAnalyzer.datasets[0].name
                              : 'Select Dataset'
                          }
                          defaultValue={
                            isEdit ? selectedAnalyzer.datasets[0].id : ''
                          }
                          options={datasetsState.map(ds => {
                            return {
                              valueField: ds.id,
                              displayField: ds.name,
                            }
                          })}
                          autoComplete='on'
                          errors={errors}
                          control={control}
                          setValue={setValue}
                          setError={setError}
                        />
                      </Col>
                    </Row>
                    <ComponentBoxFooter>
                      <ICSButton
                        id='formSubmit'
                        width={60}
                        useSubmitBehavior={true}
                        type='success'
                        disabled={!isValid || analyzersError?.code === 'invalid'}
                      >
                        Save
                      </ICSButton>
                    </ComponentBoxFooter>
                  </FormGroup>
                </Form>
              )}
            </Col>
          </Row>

        </ModalBody>

      </ICSModal>
    </>
  )
}

export default AnalyzerGenerator
